import { useEffect, useRef, useState } from "react";
import { motion, useAnimation, useInView } from "framer-motion";
import { CgTwilio } from "react-icons/cg";
import { FaCode, FaGlobe, FaPlay, FaArrowRight } from "react-icons/fa";
import { Link } from "react-router-dom";
import { CTABanner } from "../../Components/PortfolioBlocks";
import BrandButton from "../../Components/BrandButton";
import { CiMobile3 } from "react-icons/ci";
import { FaMobileScreenButton } from "react-icons/fa6";
import { RiMobileDownloadFill } from "react-icons/ri";

const ctaAccent = { base: "#C2410C", hover: "#9A3412", dark: "#7C2D12", soft: "#FFF7ED", rgb: "124, 45, 18" };

export const MobileApps = () => {

  const audioRef = useRef(null);
  const progressBarRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isEnded, setIsEnded] = useState(false);



  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    show: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.15,
        duration: 0.6,
        ease: "easeOut",
      },
    }),
  };

  const fadeUp2 = {
    hidden: { opacity: 0, y: 30 },
    visible: (delay = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut", delay },
    }),
  };

  const headingRef = useRef(null);
  const paragraphRef = useRef(null);

  const headingControls = useAnimation();
  const paragraphControls = useAnimation();

  const isHeadingInView = useInView(headingRef, { once: true, amount: 0.4 });
  const isParagraphInView = useInView(paragraphRef, {
    once: true,
    amount: 0.4,
  });

  useEffect(() => {
    if (isHeadingInView) {
      headingControls.start("visible");
    }
  }, [isHeadingInView]);

  useEffect(() => {
    if (isParagraphInView) {
      paragraphControls.start("visible");
    }
  }, [isParagraphInView]);

 const imageFrames = [
  "/mob1.webp",
  "/mob2.webp",
  "/mob3.webp",
  "/mob4.webp",
  "/mob5.webp",
  "/mob6.webp",
  "/mob7.webp",
  "/mob8.webp",
  "/mob9.webp",
];

  const [index, setIndex] = useState(0);
  const platformRef = useRef(null);
  const width = 1050;

  const next = () => {
    if (index < imageFrames.length - 1) setIndex(index + 1);
  };

  const prev = () => {
    if (index > 0) setIndex(index - 1);
  };

  const goTo = (target) => {
    if (target === "end") setIndex(imageFrames.length - 1);
    else if (!isNaN(target)) setIndex(Number(target));
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [index]);

  useEffect(() => {
    if (platformRef.current) {
      platformRef.current.style.right = `${index * width}px`;
    }
  }, [index]);


  const [callState, setCallState] = useState("incoming");


  return (
    <section className="text-black">
      <div className="mt-20 font-sans overflow-x-hidden relative ">
        <section className="relative z-10 py-16 md:py-24 px-6 md:px-12 lg:px-24">
          <div className="max-w-7xl text-center lg:text-left mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={0}
              >
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#f974161c]  text-primary-dark font-medium">
                  <span className="relative flex h-3 w-3 mr-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#F97316] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-primary-dark"></span>
                  </span>
                  <span className="text-[#F97316]">Mobile Apps</span>
                </div>
              </motion.div>
              <motion.h1
                className="text-4xl sm:text-4xl md:text-5xl lg:text-6xl text-center lg:text-left font-display font-bold tracking-tight text-gray-900"
                initial={{ y: 20 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              >
                Next-Gen Mobile Apps{" "}
                <span className="text-[#F97316]">
                  Built for Your Business
                </span>
              </motion.h1>

              <motion.p
                  className="text-lg md:text-xl text-gray-600 max-w-2xl mt-2 text-center lg:text-left md:text-center"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
                >
                We create fast, intuitive, and scalable mobile apps that bring
                your ideas to life - from customer engagement platforms and
                delivery apps to enterprise tools and on-demand services.
                <span className="ml-2 font-semibold">
                  Drive growth, boost retention, and deliver a seamless
                  experience on every device.
                </span>
              </motion.p>

              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={3}
                className="flex flex-wrap gap-4 justify-center lg:justify-start"
              >
                <BrandButton as={Link} to="/contact" accent={ctaAccent}>
                Get a Free Quote
              </BrandButton>
              </motion.div>

              <motion.p
                className="text-sm md:text-md text-gray-600 max-w-2xl"
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={4}
              >
                See how our mobile apps empower businesses - smarter workflows,
                happier users, and measurable results.
              </motion.p>
            </div>

            <div className="relative">
              <motion.div
                initial={{ y: 30 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="relative w-full max-w-md mx-auto rounded-3xl shadow-2xl bg-[#f9741631] border border-gray-100 p-6"
              >
                <div className="bg-white/80 backdrop-blur-sm border border-gray-100 rounded-2xl  text-center relative">
                  <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    animate="show"
                    custom={4}
                  className="absolute -top-10 p-8 text-5xl -right-20 text-[#F97316] rounded-2xl bg-[#f9741623] backdrop-blur-sm shadow-lg z-20 hidden sm:block"
                  >
                    <FaMobileScreenButton />
                  </motion.div>

                  <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    animate="show"
                    custom={6}
                   className="absolute top-64 p-4 text-5xl -left-14 text-[#F97316] rounded-2xl bg-[#f9741623] backdrop-blur-sm shadow-lg z-20 hidden sm:block"
                  >
                    <RiMobileDownloadFill />
                  </motion.div>

                  <div className="">
                    <div className="w-full  rounded-xl overflow-hidden shadow-inner ">
                      <img
                        src="/mobile2.webp"
                        alt="WebApp LED Display"
                        width="800"
                        height="626"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
      {/* <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-10 pb-16 text-center lg:pt-20">
        <motion.p
          className="mx-auto -mt-4 max-w-2xl text-md tracking-widest text-slate-700 sm:mt-6"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          Our
          <span className="border-b border-dotted border-slate-300 ml-2">
            Work
          </span>
        </motion.p>
 
        <motion.h1
          className="mx-auto mt-4 max-w-4xl font-display text-5xl font-semibold tracking-tight text-slate-900 sm:text-7xl leading-tight"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.3 }}
          viewport={{ once: true }}
        >
          <span className="inline-block">Web Apps We Build </span>
        </motion.h1>
 
        <motion.p
          className="mx-auto mt-9 max-w-2xl text-lg tracking-tight text-slate-600 sm:mt-6"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.6 }}
          viewport={{ once: true }}
        >
          Explore a showcase of powerful, production-ready web applications we've built - each crafted for speed, scalability, and an exceptional user experience.        </motion.p>
 
        <motion.div
          className="gallery h-full rounded mx-auto mt-20"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.9 }}
          viewport={{ once: true }}
        >
          <div class="w-full">
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 h-full">
              <div class="col-span-2 sm:col-span-1 md:col-span-2 bg-gray-50 h-auto md:h-full flex flex-col">
                <a href="https://www.autotrader.co.uk/" target="_blank" rel="noopener noreferrer"
                  class="group relative flex flex-col overflow-hidden rounded-lg px-4 pb-4 pt-40 flex-grow">
 
                  <img src="/g1.png" alt="" class="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500 ease-in-out" />
 
                  <div class="absolute inset-0 bg-gradient-to-b from-gray-900/25 to-gray-900/5 z-10"></div>
 
                  <div class="absolute inset-0 bg-[#F97316]/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-20"></div>
 
                  <h3 class="z-30 text-2xl font-medium text-white absolute top-0 left-0 p-4 xs:text-xl md:text-3xl">
                    Auto Trader
                  </h3>
                </a>
              </div>
              <div class="col-span-2 sm:col-span-1 md:col-span-2 bg-stone-50">
                <a href="https://www.cargurus.co.uk/" target="_blank" rel="noopener noreferrer"
                  class="group relative flex flex-col overflow-hidden rounded-lg px-4 pb-4 pt-40 mb-4">
 
                  <img src="/g2.png" alt="" class="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500 ease-in-out" />
 
                  <div class="absolute inset-0 bg-gradient-to-b from-gray-900/25 to-gray-900/5 z-10"></div>
 
                  <div class="absolute inset-0 bg-[#F97316]/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-20"></div>
 
                  <h3 class="z-30 text-2xl font-medium text-white absolute top-0 left-0 p-4 xs:text-xl md:text-3xl">
                    Cargurus
                  </h3>
                </a>
 
                <div class="grid gap-4 grid-cols-2 sm:grid-cols-2 lg:grid-cols-2">
                  <a href="https://abc6onyourside.com/" target="_blank" rel="noopener noreferrer"
                    class="group relative flex flex-col overflow-hidden rounded-lg px-4 pb-4 pt-40">
 
                    <img src="/g4.png" alt="" class="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500 ease-in-out" />
 
                    <div class="absolute inset-0 bg-gradient-to-b from-gray-900/25 to-gray-900/5 z-10"></div>
 
                    <div class="absolute inset-0 bg-[#F97316]/40   opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-20"></div>
 
                    <h3 class="z-30 text-2xl font-medium text-white absolute top-0 left-0 p-4 xs:text-xl md:text-3xl">
                      ABC6OnYourSide
                    </h3>
                  </a>
 
                  <a href="https://eatigo.com/en" target="_blank" rel="noopener noreferrer"
                    class="group relative flex flex-col overflow-hidden rounded-lg px-4 pb-4 pt-40">
 
                    <img src="/g5.png" alt="" class="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500 ease-in-out" />
 
                    <div class="absolute inset-0 bg-gradient-to-b from-gray-900/25 to-gray-900/5 z-10"></div>
 
                    <div class="absolute inset-0 bg-[#F97316]/40   opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-20"></div>
 
                    <h3 class="z-30 text-2xl font-medium text-white absolute top-0 left-0 p-4 xs:text-xl md:text-3xl">
                      Eatigo
                    </h3>
                  </a>
 
                </div>
              </div>
              <div class="col-span-2 sm:col-span-1 md:col-span-1 bg-sky-50 h-auto md:h-full flex flex-col">
                <a href="https://industry4-01.com/" target="_blank" rel="noopener noreferrer"
                  class="group relative flex flex-col overflow-hidden rounded-lg px-4 pb-4 pt-40 flex-grow">
 
                  <img src="/g3.png" alt="" class="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500 ease-in-out" />
 
                  <div class="absolute inset-0 bg-gradient-to-b from-gray-900/25 to-gray-900/5 z-10"></div>
 
                  <div class="absolute inset-0 bg-[#F97316]/40  opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-20"></div>
 
                  <h3 class="z-30 text-2xl font-medium text-white absolute top-0 left-0 p-4 xs:text-xl md:text-3xl">
                    Industry4-01
                  </h3>
                </a>
 
              </div>
            </div>
          </div>
        </motion.div>
      </div> */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-10 pb-16 text-center lg:pt-20">
        <motion.p
          className="mx-auto -mt-4 max-w-2xl text-md tracking-widest text-slate-700 sm:mt-6"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          Our
          <span className="border-b border-dotted border-slate-300 ml-2">
            Work
          </span>
        </motion.p>

        <motion.h2
          className="mx-auto mt-4 max-w-4xl font-display text-3xl sm:text-3xl md:text-5xl font-semibold tracking-tight text-slate-900  leading-tight"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.3 }}
          viewport={{ once: true }}
        >
          <span className="inline-block">Some Of Our Mobile Apps </span>
        </motion.h2>

        <motion.p
          className="mx-auto mt-9 max-w-2xl text-lg tracking-tight text-slate-600 sm:mt-6"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.6 }}
          viewport={{ once: true }}
        >
          Explore a showcase of powerful, production-ready mobile apps we’ve
          built - each crafted for top performance, intuitive user experiences,
          and seamless functionality across devices.{" "}
        </motion.p>

        <motion.div
          className="gallery h-full rounded mx-auto m-5 hidden md:block"
          style={{ width: `${width}px` }}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.9 }}
          viewport={{ once: true }}
        >
          <div className="content-area w-full h-full overflow-hidden">
            <div
              className="platform flex transition-all duration-300 relative"
              style={{
                width: `${width * imageFrames.length}px`,
                right: `${index * width}px`,
              }}
              ref={platformRef}
            >
              {imageFrames.map((src, i) => (
                <div
                  key={i}
                  className="each-frame flex-none h-full flex items-center justify-center"
                  style={{ width: `${width}px` }}
                >
                  <img
                      src={src}
                      loading="lazy"
                      decoding="async"
                      className="h-full border-2 rounded-2xl object-contain"
                      alt={`Frame ${i + 1}`}
                    />
                </div>
              ))}
            </div>
          </div>
          <div className="top flex p-2 items-center justify-center">
            <div className="buttons flex gap-3 text-gray-600 mr-1">
              <motion.svg
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 1.1 }}
                onClick={prev}
                className="w-10 rounded-lg bg-[#F97316] p-2 cursor-pointer"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="#fff"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </motion.svg>
              <motion.svg
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 1.1 }}
                onClick={next}
                className="w-10 rounded-lg bg-[#F97316] p-2 cursor-pointer"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="#fff"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </motion.svg>
            </div>
          </div>
        </motion.div>
        {/* Column layout for small screens */}
        <div className="flex flex-col md:hidden mt-8">
          {imageFrames.map((src, i) => (
            <div
              key={i}
              className="w-full flex items-center justify-center mb-4"
            >
             <img
                src={src}
                width="1024"
                height="638"
                loading="lazy"
                decoding="async"
                className="w-full h-auto border-2 rounded-2xl object-contain"
                alt={`Frame ${i + 1}`}
              />
            </div>
          ))}
        </div>
      </div>

      <CTABanner
        title="Have a mobile app idea?"
        text="Tell us what you want to build, from customer apps to delivery and on-demand services, and we'll send you a free quote."
        buttonLabel="Get a Free Quote"
        color="#C2410C"
        hoverBg="#fff7ed"
      />
    </section>
  )
}
